import React, { useMemo } from 'react';
import { format, isToday } from 'date-fns';
import { AlertTriangle, Plus, Home, Building2, Loader2 } from 'lucide-react';
import { CalendarShiftCard } from './CalendarShiftCard';
import { continuationsForDay } from './calendarUtils';

const ROW_LABELS = { participant: 'participants', staff: 'staff', sil: 'SIL houses', centre: 'centre sessions' };
const isDrillable = (row) => (row.kind === 'sil' || row.kind === 'centre') && row.id !== 'no_house' && row.id !== 'no_session';

// Deputy-style rows × days grid (Week view). Rows + shifts + totals come pre-aggregated and
// server-paginated from GET /roster/board; "Load more" asks the backend for the next page.
export function CalendarWeekView({
  days, rows, openShifts, dailyTotals, totalRows, hasMore, onLoadMore, onShowAll, loadingMore,
  groupBy, searchTerm = '', onShiftClick, onCellClick, onDragDrop, onRowClick, permissions,
}) {
  const canDrag = permissions.includes('roster.update') || permissions.includes('roster.manage');
  const dayKeys = useMemo(() => days.map(d => format(d, 'yyyy-MM-dd')), [days]);

  const openByDay = useMemo(() => {
    const map = {};
    (openShifts || []).forEach(s => { (map[s.date] ||= []).push(s); });
    return map;
  }, [openShifts]);
  const totalOpen = (openShifts || []).length;

  const handleDragStart = (e, shift) => {
    e.dataTransfer.setData('shiftId', shift.id);
    e.dataTransfer.setData('shiftDate', shift.date);
  };
  const handleDrop = (e, newDate) => {
    e.preventDefault();
    const shiftId = e.dataTransfer.getData('shiftId');
    const oldDate = e.dataTransfer.getData('shiftDate');
    if (shiftId && oldDate !== newDate) onDragDrop(shiftId, newDate);
  };
  const handleDragOver = (e) => e.preventDefault();

  return (
    <div>
      {/* Header */}
      <div className="flex sticky top-0 z-20 bg-white border-b">
        <div className="w-48 flex-shrink-0 p-2 border-r text-[10px] text-muted-foreground flex items-end" data-testid="calendar-rows-count">
          {totalRows > 0 ? `${rows.length} of ${totalRows} ${ROW_LABELS[groupBy] || 'rows'}` : ''}
        </div>
        {days.map(day => (
          <div key={day.toISOString()} className={`flex-1 min-w-[120px] text-center px-1 py-1.5 border-r ${isToday(day) ? 'bg-blue-50' : ''}`}>
            <p className={`text-[10px] uppercase tracking-wider ${isToday(day) ? 'text-blue-600 font-bold' : 'text-muted-foreground'}`}>{format(day, 'EEE')}</p>
            <p className={`text-sm font-semibold ${isToday(day) ? 'text-blue-700' : 'text-slate-800'}`}>{format(day, 'd MMM')}</p>
          </div>
        ))}
      </div>

      {/* Open Shifts */}
      {totalOpen > 0 && (
        <div className="flex border-b bg-red-50/30" data-testid="open-shifts-row">
          <div className="w-48 flex-shrink-0 p-2 border-r bg-red-50 sticky left-0 z-10">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center"><AlertTriangle className="w-3.5 h-3.5 text-red-500" /></div>
              <div><p className="text-xs font-medium text-red-700">Open Shifts</p><p className="text-[10px] text-red-500" data-testid="open-shifts-count">{totalOpen} unfilled</p></div>
            </div>
          </div>
          {dayKeys.map((dk, i) => (
            <div key={dk} className={`flex-1 min-w-[120px] p-1 border-r ${isToday(days[i]) ? 'bg-blue-50/30' : ''}`}
              onDrop={e => handleDrop(e, dk)} onDragOver={handleDragOver}>
              {(openByDay[dk] || []).map(s => <CalendarShiftCard key={s.id} shift={s} onClick={onShiftClick} compact={(openByDay[dk] || []).length > 2} showParticipant draggable={canDrag} onDragStart={handleDragStart} />)}
            </div>
          ))}
        </div>
      )}

      {/* Rows */}
      {rows.map(row => {
        const byDay = {};
        (row.shifts || []).forEach(s => { (byDay[s.date] ||= []).push(s); });
        return (
          <div key={row.id} className="flex border-b hover:bg-slate-50/50 transition-colors" data-testid={`row-${row.id}`}>
            <div
              className={`w-48 flex-shrink-0 p-2 border-r bg-white sticky left-0 z-10 ${isDrillable(row) ? 'cursor-pointer hover:bg-slate-100' : ''}`}
              onClick={() => { if (isDrillable(row) && onRowClick) onRowClick(row); }}
              data-testid={`row-label-${row.id}`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${row.kind === 'sil' ? 'bg-emerald-100 text-emerald-700' : row.kind === 'centre' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>
                  {row.kind === 'sil' ? <Home className="w-3.5 h-3.5" /> : row.kind === 'centre' ? <Building2 className="w-3.5 h-3.5" /> : row.initials}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{row.label}</p>
                  <p className="text-[10px] text-muted-foreground">{Number(row.hours || 0).toFixed(1)}h{isDrillable(row) ? ' · view' : ''}</p>
                </div>
              </div>
            </div>
            {dayKeys.map((dk, i) => {
              const dayShifts = byDay[dk] || [];
              const continuations = continuationsForDay(row.shifts, dk);
              return (
                <div key={dk} className={`flex-1 min-w-[120px] p-1 border-r ${isToday(days[i]) ? 'bg-blue-50/40' : ''}`}
                  onDrop={e => handleDrop(e, dk)} onDragOver={handleDragOver}>
                  {continuations.map(s => <CalendarShiftCard key={`cont-${s.id}`} shift={s} onClick={onShiftClick} />)}
                  {dayShifts.map(s => <CalendarShiftCard key={s.id} shift={s} onClick={onShiftClick} compact={dayShifts.length > 2} draggable={canDrag} onDragStart={handleDragStart} />)}
                  {dayShifts.length === 0 ? (
                    <div className="h-full min-h-[40px] flex items-center justify-center cursor-pointer group" onClick={() => onCellClick(row, dk)} data-testid={`add-shift-${row.id}-${dk}`}><span className="w-6 h-6 rounded-full bg-blue-50 border border-blue-300 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors"><Plus className="w-4 h-4" /></span></div>
                  ) : (
                    <div className="flex items-center justify-center py-1 cursor-pointer border border-dashed border-blue-300 text-blue-600 rounded mt-1 bg-blue-50/60 hover:bg-blue-600 hover:text-white transition-colors" onClick={() => onCellClick(row, dk)} data-testid={`add-shift-more-${row.id}-${dk}`}><Plus className="w-3.5 h-3.5" /></div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {rows.length === 0 && (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="calendar-no-rows">
          {searchTerm ? `No ${ROW_LABELS[groupBy] || 'rows'} match "${searchTerm}"` : 'Nothing to display for this period'}
        </div>
      )}

      {/* Load more rows — next page from the backend */}
      {hasMore && (
        <div className="flex border-b bg-slate-50/60">
          <div className="w-full p-2 flex items-center justify-center gap-4">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-60"
              data-testid="calendar-load-more-rows"
            >
              {loadingMore && <Loader2 className="w-3 h-3 animate-spin" />}
              {loadingMore ? 'Loading…' : `Load more (${Math.max(0, totalRows - rows.length)} more)`}
            </button>
            {onShowAll && (
              <button onClick={onShowAll} disabled={loadingMore}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-60"
                data-testid="calendar-show-all-rows">
                Show all ({totalRows})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Daily Totals (range-wide, from backend) */}
      <div className="flex border-t-2 bg-slate-50 sticky bottom-0" data-testid="daily-totals-row">
        <div className="w-48 flex-shrink-0 p-2 border-r font-medium text-xs text-slate-600 sticky left-0 bg-slate-50 z-10">Daily Totals</div>
        {dayKeys.map((dk, i) => {
          const t = (dailyTotals || {})[dk] || { shifts: 0, hours: 0 };
          return (
            <div key={dk} className={`flex-1 min-w-[120px] p-2 border-r text-center ${isToday(days[i]) ? 'bg-blue-50/50' : ''}`} data-testid={`daily-total-${dk}`}>
              <p className="text-xs font-bold text-slate-700">{t.shifts} shifts</p>
              <p className="text-[10px] text-muted-foreground">{Number(t.hours || 0).toFixed(1)}h</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
